// The composer card: the box at the foot of the conversation that the reader
// types into. ONE component for the session composer (AgentChat) and
// Platinum's start screen, so the two behave and look the same — the same
// pill model picker, the same clip and mic, the same chip strip, the same
// hover lift (Adam, 11 Sep: "the composer needs to behave and act the same
// in both"). Each host supplies what differs through slots: a leading
// control (the start screen's Session options), the toolbar it wires to its
// own attachments and dictation, a trailing element (the session's status
// line), and the Send/Stop button state.
//
// The card is DOCKED: it meets the bottom edge, so only the top corners are
// rounded, there is no border beneath it and no shadow — the thin border is
// the whole edge (Adam, 10 Sep). Under the pointer, or with the caret in it,
// it rises by COMPOSER_HOVER_LIFT_PX: padding-bottom grows and an equal
// negative margin-top keeps its layout height fixed, so only the card moves,
// never the list behind it (Adam, 11 Sep). Left alone it sinks the other way
// by COMPOSER_REST_DROP_PX — margin-top grows and padding-bottom shrinks by
// the same amount, so the card sits lower in the edge and the layout still
// does not move (Adam, 12 Sep: "drop the composer when it isn't being used,
// slow the animation a touch, keep the hover position as it is"). Full width
// it flattens into a bar; with both side panels open its top is straight.
import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, MenuItem, Select, Typography } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import ComposerChipStrip from './ComposerChipStrip.js'
import { EDGE_STRIP_WIDTH, EDGE_SLIDE_MS, EDGE_RETREAT_MS, EDGE_REVEAL_EASING, EDGE_RETREAT_EASING } from './EdgeReveal.js'
import { COMPOSER_RADIUS, HAIRLINE, HAIRLINE_COLOR, HAIRLINE_HOVER_COLOR, INNER_RADIUS, PILL_RADIUS } from './surface.js'
import type { AgentComposerChip } from '../plugins.js'

export const COMPOSER_CARD_RADIUS = `${COMPOSER_RADIUS} ${COMPOSER_RADIUS} 0 0`
export const COMPOSER_CARD_BORDER = HAIRLINE
/** The card's vertical padding, and how far its top rises under the pointer. */
export const COMPOSER_CARD_PAD_Y = 10
export const COMPOSER_HOVER_LIFT_PX = 12
/** How far the card sinks into the edge when nobody is using it (Adam, 12 Sep). */
export const COMPOSER_REST_DROP_PX = 1
/** Rising takes a touch longer than the edge strip; sinking back is quicker but unhurried. */
// Slower than the edge strip (Adam, 13 Sep: "slow down the composer animation
// when it is hovered"): the card is a large thing to move, and a quick lift
// under the pointer reads as a twitch.
export const COMPOSER_LIFT_MS = EDGE_SLIDE_MS * 2 + 80
export const COMPOSER_DROP_MS = EDGE_RETREAT_MS * 3 + 100
/** The controls row straddles the card's top edge: this much of it stands above (Adam, 13 Sep). */
export const COMPOSER_OVERHANG_PX = 20
const CONTROL_SIZE = COMPOSER_OVERHANG_PX * 2
/** The circle is taller than the controls, so it reaches deeper into the card: the field starts below it, with a gap (Adam, 13 Sep). */
const OVERHANG_INSET_PX = 28 + 12
/** Send and Stop: one round icon button at the right-hand end of the input row. */
/** The row's side padding per shape: the bar shape keeps clear of the edge strip. */
export const composerPadX = (shape: ComposerCardShape) => (shape === 'bar' ? EDGE_STRIP_WIDTH + 16 : 16)
/** Where the Send / Stop button's centre sits, measured in from the card's right edge. */
export const composerButtonCentreInset = (shape: ComposerCardShape) => composerPadX(shape) + COMPOSER_BUTTON_SX.minWidth / 2

export const COMPOSER_BUTTON_SX = { borderRadius: PILL_RADIUS, minWidth: 52, height: 40, flexShrink: 0, textTransform: 'none' } as const

/**
 * How long the Stop button stays armed after the first click (Matt, 11 Sep):
 * "first click shows are you sure, second click stops". Stopping a turn
 * throws away minutes of work, and the button sits where Send sits a moment
 * later — a mis-click there is easy and expensive.
 *
 * Twice the double-Escape window in <AgentChat>, deliberately. Two Escapes
 * are one reflex and 1.5s is generous for them; a click asks the reader to
 * notice the button changed, read a word, and move the mouse back.
 */
export const STOP_CONFIRM_WINDOW_MS = 3000

export type ComposerCardShape = 'docked' | 'square' | 'bar' | 'plain'

export interface ComposerModel { id: string; label: string; costMultiplier?: number }

export function formatCostMultiplier(m: number): string {
  return `${Number.isInteger(m) ? m : m.toFixed(1)}×`
}

export interface ComposerCardProps {
  testId?: string
  /** 'docked' is the card; 'square' its straight-topped form (both panels
   *  open); 'bar' the full-width flattened form; 'plain' the pre-sheet layout. */
  shape?: ComposerCardShape
  /** A soft upward shadow for a card floating on a photograph. */
  elevated?: boolean
  /** Render as a form (the session composer submits on Enter through it). */
  component?: 'div' | 'form'
  onSubmit?: (e: React.FormEvent) => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent) => void
  /** A control before the model picker, e.g. Session options. */
  leading?: React.ReactNode
  models: ComposerModel[]
  selectedModel: string
  onModelChange: (id: string) => void
  modelDisabled?: boolean
  /** Show the picker even with one model. Default: only when there is a choice. */
  alwaysShowModelPicker?: boolean
  /** The host's <ChatInputToolbar>, wired to its attachments and dictation. */
  toolbar: React.ReactNode
  chips?: AgentComposerChip[]
  onRemoveChip?: (id: string) => void
  onChipClick?: (id: string) => void
  /** At the right-hand end of row one: the session's status line. */
  trailing?: React.ReactNode
  /** The living circle: sits astride the card's top edge at the right (Adam, 13 Sep). */
  badge?: React.ReactNode
  textareaRef?: React.Ref<HTMLTextAreaElement>
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onPaste?: (e: React.ClipboardEvent) => void
  placeholder?: string
  /** Resting depth of the field in text rows. Default 1; the start screen asks for more. */
  minRows?: number
  disabled?: boolean
  /** Send is the default; 'stop' while the agent is working; 'busy' spins. */
  action?: 'send' | 'stop' | 'busy'
  sendDisabled?: boolean
  onStop?: () => void
  /**
   * A border colour for the card (docked and square shapes), drawn
   * COMPOSER_ACCENT_PX wide in place of the hairline, so it lifts with the
   * card. A theme path ('info.main') or any CSS colour. The Help pane marks
   * its composer this way (Adam, 14 Oct).
   */
  accentColor?: string
}

/** The width of an accented card's border. */
export const COMPOSER_ACCENT_PX = 4

export default function ComposerCard({
  testId, shape = 'docked', elevated, component = 'div', onSubmit, onDragOver, onDrop,
  leading, models, selectedModel, onModelChange, modelDisabled, alwaysShowModelPicker,
  toolbar, chips, onRemoveChip, onChipClick, trailing, badge,
  textareaRef, value, onChange, onKeyDown, onPaste, placeholder = 'Type a message...', minRows = 1, disabled,
  action = 'send', sendDisabled, onStop, accentColor,
}: ComposerCardProps) {
  const showModelPicker = alwaysShowModelPicker ? models.length > 0 : models.length > 1

  // Stop asks first. Armed by the first click, disarmed by the second, by the
  // window running out, or by the turn ending on its own — that last one is a
  // render-phase reset rather than an effect, so an armed button is never
  // painted over a turn that has already finished.
  const [stopArmed, setStopArmed] = useState(false)
  if (action !== 'stop' && stopArmed) setStopArmed(false)
  useEffect(() => {
    if (!stopArmed) return
    const t = window.setTimeout(() => setStopArmed(false), STOP_CONFIRM_WINDOW_MS)
    return () => window.clearTimeout(t)
  }, [stopArmed])
  const handleStopClick = () => {
    if (!stopArmed) {
      setStopArmed(true)
      return
    }
    setStopArmed(false)
    onStop?.()
  }
  const padX = composerPadX(shape)
  // The plain shape has no edge to straddle; every card shape does.
  const overhang = shape !== 'plain'
  const shell = shape === 'plain'
    ? { borderTop: '1px solid #e5e7eb' }
    : shape === 'bar'
      ? { mb: 0, backgroundColor: 'background.paper', border: 'none', borderTop: COMPOSER_CARD_BORDER, borderRadius: 0, px: `${padX}px` }
      : {
          mb: 0,
          backgroundColor: 'background.paper',
          ...(accentColor
            ? { border: `${COMPOSER_ACCENT_PX}px solid`, borderColor: accentColor }
            : { border: COMPOSER_CARD_BORDER }),
          borderBottom: 'none',
          borderRadius: shape === 'square' ? 0 : COMPOSER_CARD_RADIUS,
          ...(elevated ? { boxShadow: '0 -8px 24px rgba(10,22,40,0.08)' } : {}),
          // At rest: sunk into the edge. The bottom padding gives up what the
          // top margin takes, so the layout height is the same as when raised.
          paddingBottom: `${COMPOSER_CARD_PAD_Y - COMPOSER_REST_DROP_PX}px`,
          marginTop: `${COMPOSER_REST_DROP_PX}px`,
          transition: `padding-bottom ${COMPOSER_DROP_MS}ms ${EDGE_RETREAT_EASING}, margin-top ${COMPOSER_DROP_MS}ms ${EDGE_RETREAT_EASING}`,
          // In use: the raised position, unchanged. Focus counts as use, so
          // typing never happens in the sunk card.
          '&:hover, &:focus-within': {
            paddingBottom: `${COMPOSER_CARD_PAD_Y + COMPOSER_HOVER_LIFT_PX}px`,
            marginTop: `-${COMPOSER_HOVER_LIFT_PX}px`,
            transition: `padding-bottom ${COMPOSER_LIFT_MS}ms ${EDGE_REVEAL_EASING}, margin-top ${COMPOSER_LIFT_MS}ms ${EDGE_REVEAL_EASING}`,
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }

  return (
    <Box
      component={component}
      onSubmit={onSubmit}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={testId}
      data-shape={shape}
      sx={{ p: `${COMPOSER_CARD_PAD_Y}px 16px`, display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative', ...shell, ...(overhang ? { pt: `${OVERHANG_INSET_PX}px` } : {}) }}
    >
      {badge && (
        // Centred on the Send / Stop button below it (Adam, 13 Sep): the
        // button is COMPOSER_BUTTON_SX.minWidth wide at the row's right
        // padding, so its centre is that padding plus half its width in.
        <Box data-testid="composer-badge" sx={{ position: 'absolute', top: -28, right: `${padX + COMPOSER_BUTTON_SX.minWidth / 2}px`, transform: 'translateX(50%)', zIndex: 3 }}>{badge}</Box>
      )}
      {/* Row one: leading control, model picker, clip and mic, then whatever
          the host puts at the right-hand end. It stands astride the card's
          top edge, centred on the same line as the living circle and the
          jump button (Adam, 13 Sep: "could the model picker/attach/mic
          buttons all overhang like the orb?"); every control gets the
          circle's white ground so it reads against the messages behind it.
          The chips live in the field below, so nothing here collides. */}
      <Box
        data-testid="composer-controls"
        data-overhang={overhang ? 'true' : 'false'}
        sx={{
          display: 'flex', gap: 1, alignItems: 'center',
          ...(overhang ? {
            position: 'absolute', top: -COMPOSER_OVERHANG_PX, left: `${padX}px`, height: CONTROL_SIZE, zIndex: 3,
            '& .MuiIconButton-root': {
              width: CONTROL_SIZE, height: CONTROL_SIZE, bgcolor: 'background.paper', border: HAIRLINE,
              '&:hover': { bgcolor: 'background.paper' },
            },
            '& .MuiSelect-root, & .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
          } : {}),
        }}
      >
        {leading}
        {showModelPicker && (
          <Select
            size="small"
            value={selectedModel}
            onChange={e => onModelChange(String(e.target.value))}
            disabled={modelDisabled}
            inputProps={{ 'aria-label': 'Model' }}
            // Collapsed, show the name only — this sits in a crowded row. The
            // cost signal belongs in the open list, where the choice is made.
            renderValue={id => models.find(m => m.id === id)?.label ?? id}
            // A pill, like the customer and job pickers in Platinum's header:
            // the hairline is its only edge.
            sx={{
              height: 36, flexShrink: 0, fontSize: 13,
              borderRadius: PILL_RADIUS,
              '& .MuiSelect-select': { py: 0.5, pl: '16px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: HAIRLINE_COLOR },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: HAIRLINE_HOVER_COLOR },
            }}
          >
            {models.map(m => (
              <MenuItem
                key={m.id}
                value={m.id}
                sx={{ fontSize: 13, gap: 2, justifyContent: 'space-between' }}
              >
                {m.label}
                {m.costMultiplier !== undefined && (
                  <Typography component="span" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: m.costMultiplier > 1 ? '#b45309' : '#9ca3af' }}>
                    {formatCostMultiplier(m.costMultiplier)}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        )}
        {toolbar}
        {trailing}
      </Box>
      {/* Row two: the field runs the width of the card and the round button
          sits at its right-hand end. Nothing here may wrap onto a second line.
          A field resting deeper than one row keeps the button on its first
          line (Adam, 12 Sep), not down at the bottom of the empty depth. */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: minRows > 1 ? 'flex-start' : 'flex-end', flexWrap: 'nowrap' }}>
        {/* The field: the chips the reader attached sit inside it, above what
            they type, the way attachments sit in a mail composer. The field
            grows by one line only while there are chips. */}
        <Box data-testid="composer-field" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {chips && onRemoveChip && chips.length > 0 && (
          <Box sx={{ display: 'flex', px: '10px', pt: '6px' }}>
            <ComposerChipStrip chips={chips} onRemove={onRemoveChip} onClick={onChipClick} />
          </Box>
        )}
        <Box
          ref={textareaRef}
          component="textarea"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={placeholder}
          rows={minRows}
          disabled={disabled}
          sx={{
            width: '100%',
            minWidth: 0,
            p: '8px 10px',
            resize: 'none',
            fontSize: 14,
            lineHeight: 1.5,
            fontFamily: 'inherit',
            outline: 'none',
            ...(shape === 'plain' ? {
              border: '1px solid #d1d5db',
              borderRadius: INNER_RADIUS,
              '&:focus': { borderColor: '#3b82f6' },
            } : {
              // One field inside the card, not a box within a box.
              border: 'none',
              color: 'text.primary',
              backgroundColor: 'transparent',
            }),
          }}
        />
        </Box>
        {action === 'stop' ? (
          <Button
            type="button"
            data-testid="composer-stop"
            data-armed={stopArmed ? 'true' : 'false'}
            aria-label={stopArmed ? 'Stop the run' : 'Stop'}
            onClick={handleStopClick}
            onBlur={() => setStopArmed(false)}
            variant="contained"
            color={stopArmed ? 'error' : 'info'}
            sx={{ ...COMPOSER_BUTTON_SX, ...(stopArmed ? { px: 1.5, gap: 0.5, fontSize: 13, fontWeight: 600 } : {}) }}
          >
            <StopIcon sx={{ fontSize: 20 }} />
            {stopArmed ? 'Sure?' : null}
          </Button>
        ) : (
          <Button type="submit" aria-label="Send" disabled={sendDisabled || action === 'busy'} variant="contained" color="info" sx={COMPOSER_BUTTON_SX}>
            {action === 'busy' ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
          </Button>
        )}
      </Box>
    </Box>
  )
}
